/**
 * Gerar documento a partir de um modelo (procuração, contrato, recibo…),
 * com os dados da pessoa/caso/processo. Prévia ao vivo, aviso de dados
 * faltantes e gravação em Documentos (PDF e/ou Word).
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ApiError } from "@/lib/api";
import { navigate } from "@/lib/router";
import { useLookups } from "@/lib/catalog";
import { Badge, EntityPicker, ErrorBox, Field, FormGrid, Loading, Modal, type EntityRef } from "./kit";
import { toast } from "./feedback";
import { Icon } from "./icons";

export interface InputDef { key: string; label: string; type: "texto" | "textolongo" | "dinheiro" | "data" | "numero" | "simnao"; required: boolean; default?: string | null }
export interface TemplateInfo {
  id: string; key: string; name: string; description: string | null; context: "pessoa" | "caso" | "processo";
  kind: "texto" | "docx"; inputs: InputDef[]; required: string[]; letterhead: boolean; fileName: string | null;
}
interface Run { text: string; bold: boolean }
export type Block =
  | { t: "p"; align: "justify" | "left" | "center" | "right"; indent: boolean; runs: Run[] }
  | { t: "title"; runs: Run[] } | { t: "section"; runs: Run[] } | { t: "blank" } | { t: "sign"; lines: Run[][] } | { t: "break" };
interface Preview {
  missing: { key: string; label: string }[]; empty: { key: string; label: string }[]; blocks: Block[] | null; client: string;
  letterhead: { id: string | null; name: string | null; source: "escolhido" | "modelo" | "unidade" | "padrao" | "nenhum" };
}
const LH_SOURCE: Record<string, string> = { modelo: "do modelo", unidade: "da unidade", padrao: "padrão", escolhido: "escolhido" };
interface GenDoc { id: string; name: string }

export type GenCtx = { personId?: string; caseId?: string; processId?: string };

function Runs({ runs }: { runs: Run[] }): JSX.Element {
  return <>{runs.map((r, i) => (r.bold ? <strong key={i}>{r.text}</strong> : <span key={i}>{r.text}</span>))}</>;
}

/** Folha A4 em miniatura com o texto do documento. */
export function Paper({ blocks }: { blocks: Block[] }): JSX.Element {
  return (
    <div className="paper">
      {blocks.map((b, i) => {
        switch (b.t) {
          case "blank": return <div key={i} className="paper-blank" />;
          case "break": return <hr key={i} className="paper-break" />;
          case "title": return <h4 key={i} className="paper-title"><Runs runs={b.runs} /></h4>;
          case "section": return <div key={i} className="paper-section"><Runs runs={b.runs} /></div>;
          case "sign": return (
            <div key={i} className="paper-sign">
              <div className="paper-sign-line" />
              {b.lines.map((l, j) => <div key={j} className={j ? "small" : ""}><Runs runs={l} /></div>)}
            </div>
          );
          default: return <p key={i} style={{ textAlign: b.align, textIndent: b.indent ? "2.5em" : undefined }}><Runs runs={b.runs} /></p>;
        }
      })}
    </div>
  );
}

function useDebounced<T>(v: T, ms = 400): T {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

export function GenerateDocModal({ ctx, onClose }: { ctx: GenCtx; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ["modelos-documento"], queryFn: () => api.get<TemplateInfo[]>("/modelos-documento") });
  const partners = useQuery({ queryKey: ["parceiros-ativos"], queryFn: () => api.get<Array<{ id: string; name: string; oabUf: string | null; oabNumber: string | null }>>("/financeiro/parceiros/ativos") });

  // Clientes do caso (para escolher quem assina quando o contexto é caso/processo).
  const proc = useQuery({
    queryKey: ["processo", ctx.processId], enabled: !!ctx.processId && !ctx.caseId,
    queryFn: () => api.get<{ case: { id: string } }>(`/processos/${ctx.processId}`),
  });
  const caseId = ctx.caseId ?? proc.data?.case?.id;
  const kase = useQuery({
    queryKey: ["caso", caseId], enabled: !!caseId && !ctx.personId,
    queryFn: () => api.get<{ persons: Array<{ role: string; person: { id: string; name: string } }> }>(`/casos/${caseId}`),
  });
  const clients = useMemo(() => (kase.data?.persons ?? []).filter((p) => p.role === "cliente").map((p) => p.person), [kase.data]);

  const [tplId, setTplId] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(ctx.personId ?? null);
  const [partnerIds, setPartnerIds] = useState<string[]>([]);
  // Outras pessoas que assinam juntas e outros processos (procuração em vários processos).
  const [morePeople, setMorePeople] = useState<EntityRef[]>([]);
  const [moreProcs, setMoreProcs] = useState<EntityRef[]>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [formats, setFormats] = useState<{ pdf: boolean; docx: boolean }>({ pdf: true, docx: true });
  const [force, setForce] = useState(false);
  const [lhChoice, setLhChoice] = useState<string>(""); // "" automático | id | "none"
  const letterheads = useQuery({ queryKey: ["timbrados"], queryFn: () => api.get<Array<{ id: string; name: string; isDefault: boolean }>>("/timbrados") });
  const [done, setDone] = useState<{ docs: GenDoc[]; warnings: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const { can } = useLookups();
  // Lançar no Financeiro junto (contrato de honorários)
  const [fin, setFin] = useState<{ on: boolean; total: string; entrada: string; parcelas: string; primeiroVencimento: string }>({ on: false, total: "", entrada: "", parcelas: "1", primeiroVencimento: new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10) });
  const feeSug = useQuery({
    queryKey: ["honorarios-sugestao", caseId, ctx.processId], enabled: !!caseId,
    queryFn: () => api.get<{ name: string; fixedAmount: string | null; entryAmount: string | null; installments: number | null; minAmount?: string | null; oabMin?: number | null; oabRefs?: Array<{ seccional: string; code: string }> } | null>(`/honorarios/sugestao?${new URLSearchParams({ ...(caseId ? { caseId } : {}), ...(ctx.processId ? { processId: ctx.processId } : {}) })}`),
  });
  useEffect(() => {
    const f = feeSug.data;
    if (f && !fin.total) setFin((x) => ({ ...x, total: f.fixedAmount ?? "", entrada: f.entryAmount ?? "", parcelas: String(f.installments ?? 1) }));
  }, [feeSug.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const money = (v: string) => v.replace(/\./g, "").replace(",", ".").trim();

  // Um cliente só: já vem escolhido. "" = todos os clientes do caso.
  useEffect(() => { if (!ctx.personId && clients.length === 1) setPersonId(clients[0]!.id); }, [clients, ctx.personId]);

  const tpl = templates.data?.find((t) => t.id === tplId) ?? null;
  const usable = (t: TemplateInfo) => t.context === "pessoa" || (t.context === "caso" && !!caseId) || (t.context === "processo" && !!ctx.processId);

  const body = {
    templateId: tplId, personId: personId || null, caseId: caseId ?? null, processId: ctx.processId ?? null,
    partnerIds, inputs, letterheadId: lhChoice || null,
    ...(morePeople.length ? { personIds: [...(personId ? [personId] : clients.map((c) => c.id)), ...morePeople.map((x) => x.id)].filter((x, i, a) => a.indexOf(x) === i) } : {}),
    ...(moreProcs.length ? { processIds: moreProcs.map((x) => x.id) } : {}),
  };
  const dBody = useDebounced(JSON.stringify(body));
  const preview = useQuery({
    queryKey: ["gerar-previa", dBody], enabled: !!tplId && (!!personId || !!caseId || morePeople.length > 0),
    queryFn: () => api.post<Preview>("/documentos/gerar/previa", JSON.parse(dBody)),
    placeholderData: (prev) => prev,
    retry: false,
  });

  const generate = async () => {
    setBusy(true); setErr(null);
    try {
      const fm = (["pdf", "docx"] as const).filter((f) => formats[f]);
      const financeiro = fin.on && showFin ? { total: money(fin.total), entrada: fin.entrada ? money(fin.entrada) : null, parcelas: Number(fin.parcelas) || 1, primeiroVencimento: fin.primeiroVencimento } : undefined;
      const r = await api.post<{ documents: GenDoc[]; warnings: string[]; contractId: string | null }>("/documentos/gerar", { ...body, formats: fm, force, financeiro });
      if (r.contractId) { await qc.invalidateQueries({ queryKey: ["financeiro"] }); r.warnings = [...r.warnings, "Contrato e parcelas lançados no Financeiro (contas a receber)."]; }
      await qc.invalidateQueries({ queryKey: ["documentos"] });
      toast.ok(r.documents.length > 1 ? `${r.documents.length} arquivos gerados` : "Documento gerado");
      setDone({ docs: r.documents, warnings: r.warnings });
    } catch (e) { setErr(e as ApiError); } finally { setBusy(false); }
  };

  const showFin = !!tpl && /contrato|honor/i.test(`${tpl.key} ${tpl.name}`) && !!caseId && can("financeiro.lancar");
  const missing = preview.data?.missing ?? [];
  const finOk = !fin.on || !showFin || (/^\d+(\.\d{1,2})?$/.test(money(fin.total)) && Number(money(fin.total)) > 0);
  const canGen = !!tplId && (formats.pdf || formats.docx) && (!missing.length || force) && !busy && finOk;
  const editPerson = personId ?? ctx.personId;

  if (done) {
    return (
      <Modal title="Documento gerado" onClose={onClose} footer={<button className="btn primary" onClick={onClose}>Concluir</button>}>
        <div className="stack gap-8">
          {done.warnings.map((w) => <div key={w} className="warn-box">{w}</div>)}
          {done.docs.map((d) => (
            <div key={d.id} className="lrow">
              <Icon name="doc" /> <span className="grow">{d.name}</span>
              <button className="btn small" onClick={() => void api.download(`/documentos/${d.id}/download?inline=1`, d.name, true)}>Abrir</button>
              <button className="btn small" onClick={() => void api.download(`/documentos/${d.id}/download`, d.name)}>Baixar</button>
            </div>
          ))}
          <div className="muted small">Salvo em Documentos {ctx.processId ? "do processo" : caseId ? "do caso" : "da pessoa"}. Gerar de novo cria uma nova versão.</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal wide title={tpl ? `Gerar — ${tpl.name}` : "Gerar documento"} onClose={onClose}
      footer={tpl && (
        <>
          <label className="row nowrap small" style={{ marginRight: "auto" }}>
            <input type="checkbox" checked={formats.pdf} onChange={(e) => setFormats({ ...formats, pdf: e.target.checked })} /> PDF
            <input type="checkbox" checked={formats.docx} onChange={(e) => setFormats({ ...formats, docx: e.target.checked })} style={{ marginLeft: 10 }} /> Word
          </label>
          <button className="btn" onClick={() => setTplId(null)}>Voltar</button>
          <button className="btn primary" disabled={!canGen} onClick={() => void generate()}>{busy ? "Gerando…" : "Gerar e salvar"}</button>
        </>
      )}>
      {!tpl ? (
        !templates.data ? <Loading /> : (
          <div className="tpl-grid">
            {templates.data.map((t) => (
              <button key={t.id} className="tpl-card" disabled={!usable(t)} onClick={() => { setTplId(t.id); setInputs({}); setForce(false); }}
                title={usable(t) ? "" : `Precisa de um ${t.context}`}>
                <Icon name="doc" />
                <div className="grow">
                  <div className="strong">{t.name}</div>
                  {t.description && <div className="muted small">{t.description}</div>}
                </div>
                {t.kind === "docx" && <Badge tone="info">Word</Badge>}
              </button>
            ))}
            {!templates.data.length && <div className="muted">Nenhum modelo. O administrador cadastra em Configurações › Documentos.</div>}
          </div>
        )
      ) : (
        <div className="gen-layout">
          <div className="stack gap-12">
            {!ctx.personId && (
              <Field label="Cliente" required>
                <select className="input" value={personId ?? ""} onChange={(e) => setPersonId(e.target.value || null)}>
                  {clients.length !== 1 && <option value="">{clients.length > 1 ? "Todos os clientes do caso" : "—"}</option>}
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Outras pessoas que assinam juntas" hint="Ex.: procuração de duas pessoas. Entram depois do cliente acima, nesta ordem.">
              <div className="stack" style={{ gap: 4 }}>
                {morePeople.length > 0 && <div className="chips">{morePeople.map((x) => <span key={x.id} className="chip">{x.label} <a onClick={() => setMorePeople(morePeople.filter((y) => y.id !== x.id))}>✕</a></span>)}</div>}
                <EntityPicker kinds={["pessoa"]} value={null} placeholder="Buscar pessoa…" onChange={(v) => { if (v && !morePeople.some((y) => y.id === v.id) && v.id !== personId) setMorePeople([...morePeople, v]); }} />
              </div>
            </Field>
            <Field label="Outros processos" hint="O modelo pode listar todos ({{processos.numeros}} ou repetir para cada processo).">
              <div className="stack" style={{ gap: 4 }}>
                {moreProcs.length > 0 && <div className="chips">{moreProcs.map((x) => <span key={x.id} className="chip">{x.label} <a onClick={() => setMoreProcs(moreProcs.filter((y) => y.id !== x.id))}>✕</a></span>)}</div>}
                <EntityPicker kinds={["processo"]} value={null} placeholder="Buscar processo…" onChange={(v) => { if (v && !moreProcs.some((y) => y.id === v.id) && v.id !== ctx.processId) setMoreProcs([...moreProcs, v]); }} />
              </div>
            </Field>
            {(partners.data?.length ?? 0) > 0 && (
              <Field label="Advogados parceiros" hint="Entram como co-outorgados / habilitados, quando o modelo usa.">
                <div className="chips">
                  {partners.data!.map((p) => {
                    const on = partnerIds.includes(p.id);
                    return (
                      <button type="button" key={p.id} className={`chip ${on ? "on" : ""}`}
                        onClick={() => setPartnerIds(on ? partnerIds.filter((x) => x !== p.id) : [...partnerIds, p.id])}>
                        {p.name}{p.oabUf ? ` · OAB/${p.oabUf}` : ""}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}
            {tpl.letterhead && tpl.kind === "texto" && (
              <Field label="Papel timbrado">
                <select className="input" value={lhChoice} onChange={(e) => setLhChoice(e.target.value)}>
                  <option value="">
                    Automático{lhChoice === "" && preview.data ? ` — ${preview.data.letterhead.name ? `${preview.data.letterhead.name} (${LH_SOURCE[preview.data.letterhead.source] ?? ""})` : "sem timbrado"}` : ""}
                  </option>
                  {(letterheads.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}{l.isDefault ? " (padrão)" : ""}</option>)}
                  <option value="none">Sem timbrado</option>
                </select>
              </Field>
            )}
            {tpl.inputs.length > 0 && (
              <FormGrid cols={1}>
                {tpl.inputs.map((d) => (
                  <Field key={d.key} label={d.label} required={d.required}
                    hint={d.default && !inputs[d.key] ? "Em branco usa o valor do cadastro." : undefined}>
                    {d.type === "simnao" ? (
                      <select className="input" value={inputs[d.key] ?? ""} onChange={(e) => setInputs({ ...inputs, [d.key]: e.target.value })}>
                        <option value="">—</option><option value="Sim">Sim</option><option value="Não">Não</option>
                      </select>
                    ) : d.type === "textolongo" ? (
                      <textarea className="input" rows={3} value={inputs[d.key] ?? ""} onChange={(e) => setInputs({ ...inputs, [d.key]: e.target.value })} />
                    ) : (
                      <input className="input" type={d.type === "data" ? "date" : "text"} inputMode={d.type === "dinheiro" || d.type === "numero" ? "decimal" : undefined}
                        placeholder={d.type === "dinheiro" ? "1.500,00" : undefined}
                        value={inputs[d.key] ?? ""} onChange={(e) => setInputs({ ...inputs, [d.key]: e.target.value })} />
                    )}
                  </Field>
                ))}
              </FormGrid>
            )}
            {showFin && (
              <div className="card compact">
                <label className="row nowrap strong"><input type="checkbox" checked={fin.on} onChange={(e) => setFin({ ...fin, on: e.target.checked })} /> Lançar no Financeiro</label>
                <div className="muted small">Cria o contrato e o título a receber (entrada + parcelas mensais) ligados ao caso.{feeSug.data ? ` Sugestão: ${feeSug.data.name}.` : ""}
                  {feeSug.data?.minAmount ? ` Mínimo do escritório: R$ ${Number(feeSug.data.minAmount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}.` : ""}
                  {feeSug.data?.oabMin != null ? ` Referência OAB (${feeSug.data.oabRefs?.map((r) => `${r.seccional} ${r.code}`).join(", ")}): R$ ${feeSug.data.oabMin.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}.` : ""}</div>
                {fin.on && (
                  <FormGrid cols={2}>
                    <Field label="Valor total (R$)" required><input className="input" inputMode="decimal" placeholder="5.000,00" value={fin.total} onChange={(e) => setFin({ ...fin, total: e.target.value })} /></Field>
                    <Field label="Entrada (R$)"><input className="input" inputMode="decimal" value={fin.entrada} onChange={(e) => setFin({ ...fin, entrada: e.target.value })} /></Field>
                    <Field label="Parcelas (após a entrada)"><input className="input" type="number" min={1} max={120} value={fin.parcelas} onChange={(e) => setFin({ ...fin, parcelas: e.target.value })} /></Field>
                    <Field label="1º vencimento"><input className="input" type="date" value={fin.primeiroVencimento} onChange={(e) => setFin({ ...fin, primeiroVencimento: e.target.value })} /></Field>
                  </FormGrid>
                )}
              </div>
            )}
            {missing.length > 0 && (
              <div className="warn-box">
                <strong>Faltam dados no cadastro:</strong>
                <ul>{missing.map((m) => <li key={m.key}>{m.label}</li>)}</ul>
                <div className="row">
                  {editPerson && <button className="btn small" onClick={() => { onClose(); navigate(`/pessoas/${editPerson}`); }}>Completar cadastro</button>}
                  <label className="row nowrap small"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> Gerar mesmo assim (sai com ____)</label>
                </div>
              </div>
            )}
            {(preview.data?.empty.length ?? 0) > 0 && (
              <div className="muted small">Em branco no documento: {preview.data!.empty.map((e) => e.label).join(", ")}.</div>
            )}
            <ErrorBox error={err ?? preview.error} />
          </div>
          <div>
            {preview.data?.blocks ? <Paper blocks={preview.data.blocks} /> : preview.isFetching ? <Loading lines={8} /> : tpl.kind === "docx"
              ? <div className="muted pad">Escolha o cliente para ver a prévia.</div>
              : <div className="muted pad">Escolha o cliente para ver a prévia.</div>}
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Botão "Gerar documento" para o topo das telas de pessoa, caso e processo. */
export function GenerateDocButton({ ctx, className = "btn" }: { ctx: GenCtx; className?: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={className} onClick={() => setOpen(true)}><Icon name="doc" size="sm" /> Gerar documento</button>
      {open && <GenerateDocModal ctx={ctx} onClose={() => setOpen(false)} />}
    </>
  );
}
